import React, { useState } from "react";

const REPLACE_RATE = 5.5; // $/sqft avg NJ tear-off + replace
const REJUV_RATE = 2.5; // $/sqft rejuvenation treatment

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function RoofSavingsCalculator() {
  const [sqft, setSqft] = useState(2200);

  const replacement = sqft * REPLACE_RATE;
  const rejuvenation = sqft * REJUV_RATE;
  const savings = replacement - rejuvenation;
  const pct = Math.round((savings / replacement) * 100);

  return (
    <div
      className="mt-20 md:mt-28 reveal"
      data-testid="roof-savings-calculator"
    >
      <div className="border border-[var(--accent)]/30 bg-[var(--bg-primary)] p-8 md:p-14">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: heading + slider */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-5">
              <span className="hairline w-10" />
              <span className="eyebrow">Savings Calculator</span>
            </div>
            <h3 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mb-6">
              What would <em className="text-[var(--accent)]">your roof</em> save?
            </h3>
            <p className="text-[var(--text-secondary)] leading-relaxed mb-10 max-w-md">
              Slide to your approximate roof size and compare a full tear-off
              replacement against a single rejuvenation treatment.
            </p>

            <div className="flex items-end justify-between mb-4">
              <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[var(--text-secondary)]">
                Roof Size
              </span>
              <span
                className="font-display text-3xl md:text-4xl text-[var(--text-primary)]"
                data-testid="calc-sqft-value"
              >
                {sqft.toLocaleString()}{" "}
                <span className="text-base text-[var(--text-secondary)]">sq ft</span>
              </span>
            </div>
            <input
              type="range"
              min="1000"
              max="6000"
              step="100"
              value={sqft}
              onChange={(e) => setSqft(Number(e.target.value))}
              className="calc-slider w-full"
              data-testid="calc-sqft-slider"
              aria-label="Roof size in square feet"
            />
            <div className="flex justify-between mt-2 font-body text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">
              <span>1,000</span>
              <span>Avg. home ≈ 2,200</span>
              <span>6,000</span>
            </div>
          </div>

          {/* Right: results */}
          <div className="lg:col-span-6 flex flex-col">
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              <div className="p-6 md:p-8 bg-[var(--bg-secondary)]">
                <div className="font-body text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)] mb-3">
                  Full Replacement
                </div>
                <div
                  className="font-display text-3xl md:text-4xl text-[var(--text-primary)] line-through decoration-red-400/60 decoration-2"
                  data-testid="calc-replacement-cost"
                >
                  {fmt(replacement)}
                </div>
              </div>
              <div className="p-6 md:p-8 bg-[var(--bg-secondary)]">
                <div className="font-body text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)] mb-3">
                  Rejuvenation
                </div>
                <div
                  className="font-display text-3xl md:text-4xl text-[var(--accent)]"
                  data-testid="calc-rejuvenation-cost"
                >
                  {fmt(rejuvenation)}
                </div>
              </div>
              <div className="col-span-2 p-6 md:p-8 bg-black/50 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="font-body text-[10px] uppercase tracking-[0.25em] text-[var(--text-secondary)] mb-3">
                    Your Estimated Savings
                  </div>
                  <div
                    className="font-display text-4xl md:text-5xl text-[var(--accent)]"
                    data-testid="calc-savings"
                  >
                    {fmt(savings)}
                  </div>
                </div>
                <div
                  className="font-display text-2xl md:text-3xl text-[var(--text-primary)] border border-[var(--accent)]/40 px-5 py-3"
                  data-testid="calc-savings-pct"
                >
                  {pct}% less
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
              <p className="font-body text-xs text-[var(--text-secondary)] max-w-sm leading-relaxed">
                Estimates based on typical NJ pricing ({`$${REPLACE_RATE.toFixed(2)}`}/sq ft
                replacement vs. {`$${REJUV_RATE.toFixed(2)}`}/sq ft treatment). Your exact
                quote follows a free on-site inspection.
              </p>
              <a href="#contact" className="btn-solid w-fit shrink-0" data-testid="calc-cta">
                Lock In My Savings
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
